import React from 'react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import './Estudios.scss';
import { SysI_Estudios, SysI_Prioridades } from '../../types/types.estudios';
import { SysIAnalitos } from '../../types/types.analitos';
import { useApiConfiguration } from '../../api/api.configuration';
import Button from 'devextreme-react/button';
import { Tooltip } from 'devextreme-react';
import DataGrid, { Column, Button as Butt, Editing, Item as ToolbarItem, Paging, Toolbar, Popup, Lookup, RequiredRule, DataGridTypes, Selection, Form, SearchPanel, HeaderFilter, Scrolling, Grouping, GroupPanel } from 'devextreme-react/data-grid';
import CustomStore from 'devextreme/data/custom_store';
import DataSource from 'devextreme/data/data_source';
import { useAppSelector } from '../../contexts/hooks';
import { GroupItem, Item as FormItem } from 'devextreme-react/form';
import { useScreenSize } from '../../utils/media-query';
import { ModalAnalytesByStudy } from './Modals/ModalAnalytesByStudy';
import { newToast } from '../../components';

const Index = () => {
  const { isMedium, isSmall, isXSmall } = useScreenSize();
  const { getEstudios, getPrioridades, newStudy, setStudy, deleteStudy, getAnalitos, updateStudiesMassive } = useApiConfiguration();

  const [data, setData] = useState<SysI_Estudios[]>([]);
  const [analitos, setAnalitos] = useState<SysIAnalitos[]>([]);
  const [prioridades, setPrioridades] = useState<SysI_Prioridades[]>([]);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [idEstudio, setIdEstudio] = useState(-1);
  const [estudio, setEstudio] = useState('');
  const [selectedRows, setSelectedRows] = useState<SysI_Estudios[]>([]);
  const [isMassiveEditMode, setIsMassiveEditMode] = useState(false);
  const [massiveUpdateData, setMassiveUpdateData] = useState<any>({});
  const [isLoading, setIsLoading] = useState(false);

  const dataGridRef = useRef<DataGrid>(null);
  const Areas = useAppSelector((x) => x.configuration.Data?.Areas);
  const laboratorios = useAppSelector((state) => state.configuration.Data?.ComboBox?.find((combo) => combo.Name === 'SysA_LaboratoriosRemisiones')?.Data || []);

  useEffect(() => {
    getEstudios().then((res) => {
      setData(res);
    });
  }, []);

  useEffect(() => {
    getPrioridades().then((res) => {
      setPrioridades(res);
    });
  }, []);

  useEffect(() => {
    getAnalitos().then((res) => {
      setAnalitos(res);
    });
  }, []);

  const customData = useMemo(() => {
    return new CustomStore({
      key: 'Es_Id',
      loadMode: 'processed',
      load() {
        return getEstudios()
          .then((res) => ({
            data: res,
            totalCount: res.totalCount,
            summary: res.summary,
            groupCount: res.groupCount,
          }))
          .catch(() => {
            throw new Error('Error de carga de datos');
          });
      },
      insert: async (values) => {
        return newStudy({ Estudio: values })
          .then((res) => {
            if (res === 105) {
              dataGridRef.current?.instance.refresh();
            }
          })
          .catch(() => {
            throw new Error('Error insertando el Estudio');
          });
      },
      update: (key, values) => {
        // Obtener el registro completo actual del grid
        const currentRow = dataGridRef.current?.instance.getVisibleRows().find((row) => row.key === key);
        const currentData = currentRow?.data;
        
        // Combinar los valores actuales con los cambios, asegurando que Es_Lr_Id siempre esté presente
        const updatedValues = {
          ...values,
          Es_Lr_Id: values.hasOwnProperty('Es_Lr_Id') ? (values.Es_Lr_Id ?? null) : (currentData?.Es_Lr_Id ?? null)
        };
        
        return setStudy({ Estudio: updatedValues, IdEstudio: key })
          .then((res) => {
            if (res === 107) {
              dataGridRef.current?.instance.refresh();
            }
          })
          .catch(() => {
            throw new Error('Error actualizando el Estudio');
          });
      },
      remove: (key) => {
        return deleteStudy({ IdEstudio: key }).then((res) => {
          if (res === 107) {
            dataGridRef.current?.instance.refresh();
          }
        });
      },
    });
  }, []);

  const customDataSrc = new DataSource({
    store: customData,
    reshapeOnPush: true,
  });

  const handleDetails = useCallback(
    (e) => {
      dataGridRef.current?.instance.refresh();
      const { data }: { data: SysI_Estudios } = e.row;
      setIsModalVisible(true);
      setIdEstudio(data.Es_Id);
      setEstudio(data.Es_Nombre);
    },
    [idEstudio]
  );

  const handleDuplicateClick = useCallback((e: any) => {
    const { data }: { data: SysI_Estudios } = e.row;
    const { Es_Id, ...rest } = data;
    const duplicated = {
      ...rest,
      Es_Nombre: `${data.Es_Nombre} (Copia)`,
      Es_Abreviatura: `${data.Es_Abreviatura}1`,
      SysA_AreasAnalisis: null,
    };
    newStudy({ Estudio: duplicated }).then((res) => {
      if (res === 105) {
        dataGridRef.current?.instance.refresh();
        newToast('SUCCESS', 'Estudio duplicado correctamente', 3);
      }
    });
  }, [newStudy]);

  const handleSelection = useCallback(({ selectedRowsData }: DataGridTypes.SelectionChangedEvent) => {
    setSelectedRows(selectedRowsData || []);
  }, []);

  const handleUpdateData = useCallback(() => {
    getEstudios().then((res) => {
      setData(res);
    });
  }, [setData]);

  const toggleMassiveEditMode = useCallback(() => {
    setIsMassiveEditMode(!isMassiveEditMode);
    setSelectedRows([]);
    setMassiveUpdateData({});

    // Limpiar selección de forma más simple
    setTimeout(() => {
      dataGridRef.current?.instance?.clearSelection();
    }, 0);
  }, [isMassiveEditMode]);

  const handleMassiveUpdate = useCallback(async () => {
    if (selectedRows.length === 0) {
      newToast('WARNING', 'Debe seleccionar al menos un estudio para actualizar', 3);
      return;
    }

    // Verificar que hay cambios para aplicar
    const hasChanges = Object.keys(massiveUpdateData).some((key) => massiveUpdateData[key] !== undefined && massiveUpdateData[key] !== null && massiveUpdateData[key] !== '');
    if (!hasChanges) {
      newToast('WARNING', 'Debe especificar al menos un campo para actualizar', 3);
      return;
    }

    setIsLoading(true);

    try {
      // Crear la lista de actualizaciones
      const estudiosActualizaciones = selectedRows.map((estudio) => ({
        Es_Id: estudio.Es_Id,
        ...massiveUpdateData,
      }));

      const resultado = await updateStudiesMassive(estudiosActualizaciones);

      if (resultado) {
        // Mostrar resultado detallado
        if (resultado.errores && resultado.errores.length > 0) {
          console.warn('Errores en actualización masiva:', resultado.errores);
        }

        // Refrescar la grilla
        dataGridRef.current?.instance.refresh();


        // Limpiar selección y datos de actualización
        setSelectedRows([]);
        setMassiveUpdateData({});
        setIsMassiveEditMode(false);
      }
    } catch (error) {
      console.error('Error en actualización masiva:', error);
      newToast('ERROR', 'Error al realizar la actualización masiva', 4);
    } finally {
      setIsLoading(false);
    }
  }, [selectedRows, massiveUpdateData, updateStudiesMassive]);

  const handleMassiveFieldChange = useCallback((field: string, value: any) => {
    setMassiveUpdateData((prev) => ({
      ...prev,
      [field]: value,
    }));
  }, []);

  const renderMassiveEditPanel = () => {
    if (!isMassiveEditMode) return null;

    return (
      <div className='estudios-massive-edit-panel'>
        <div className='estudios-panel-header'>
          <h4>Edición Masiva {selectedRows.length > 0 && `(${selectedRows.length} estudios seleccionados)`}</h4>
        </div>

        <div className='estudios-form-grid'>
          <div className='estudios-form-field'>
            <label>Área:</label>
            <select value={massiveUpdateData.Es_Aa_Id || ''} onChange={(e) => handleMassiveFieldChange('Es_Aa_Id', e.target.value ? parseInt(e.target.value) : undefined)}>
              <option value=''>-- Sin cambios --</option>
              {Areas?.map((area) => (
                <option key={area.Aa_Id} value={area.Aa_Id}>
                  {area.Aa_Nombre}
                </option>
              ))}
            </select>
          </div>

          <div className='estudios-form-field'>
            <label>Prioridad:</label>
            <select value={massiveUpdateData.Es_Pd_Id || ''} onChange={(e) => handleMassiveFieldChange('Es_Pd_Id', e.target.value ? parseInt(e.target.value) : undefined)}>
              <option value=''>-- Sin cambios --</option>
              {prioridades?.map((prioridad) => (
                <option key={prioridad.Pd_Id} value={prioridad.Pd_Id}>
                  {prioridad.Pd_Nombre}
                </option>
              ))}
            </select>
          </div>

          <div className='estudios-form-field'>
            <label>Lab. Remisión:</label>
            <select value={massiveUpdateData.Es_Lr_Id || ''} onChange={(e) => handleMassiveFieldChange('Es_Lr_Id', e.target.value ? parseInt(e.target.value) : undefined)}>
              <option value=''>-- Sin cambios --</option>
              {laboratorios?.map((lab) => (
                <option key={lab.Lr_Id} value={lab.Lr_Id}>
                  {lab.Lr_Nombre}
                </option>
              ))}
            </select>
          </div>

          <div className='estudios-form-field'>
            <label>Estado:</label>
            <select value={massiveUpdateData.Es_Estado !== undefined ? massiveUpdateData.Es_Estado.toString() : ''} onChange={(e) => handleMassiveFieldChange('Es_Estado', e.target.value !== '' ? e.target.value === 'true' : undefined)}>
              <option value=''>-- Sin cambios --</option>
              <option value='true'>Activo</option>
              <option value='false'>Inactivo</option>
            </select>
          </div>
        </div>

        <div className='estudios-actions-container'>
          <Button text='Aplicar Cambios' type='default' stylingMode='contained' onClick={handleMassiveUpdate} disabled={isLoading || selectedRows.length === 0} icon={isLoading ? undefined : 'check'} />
          <Button text='Cancelar' type='normal' stylingMode='outlined' onClick={toggleMassiveEditMode} disabled={isLoading} />
        </div>

        {selectedRows.length === 0 && <p className='estudios-selection-info'>Seleccione los estudios que desea modificar en la tabla</p>}
      </div>
    );
  };

  return (
    <div className='view crm-contact-list'>
      <div className='view-wrapper view-wrapper-contact-list'>
        {renderMassiveEditPanel()}

        <DataGrid className='grid estudios-grid' ref={dataGridRef} dataSource={customDataSrc} width={'100%'}  height="100vh" repaintChangesOnly={true} highlightChanges={true} rowAlternationEnabled={true} showColumnLines={true} onSelectionChanged={handleSelection}>
          <SearchPanel visible={true} />
          <Scrolling columnRenderingMode='standard' mode='virtual' useNative={true} scrollByContent={true} showScrollbar="always" />
          <Selection mode={isMassiveEditMode ? 'multiple' : 'single'} />

          {/* Configuración de agrupación */}
          <Grouping autoExpandAll={true} />

          <Editing mode='popup' allowAdding={!isMassiveEditMode} allowDeleting={!isMassiveEditMode} allowUpdating={!isMassiveEditMode} texts={{ saveRowChanges: 'Guardar', cancelRowChanges: 'Cancelar', deleteRow: 'Eliminar', addRow: 'Agregar' }}>
            <Popup title='INFORMACIÓN ESTUDIO' width={1200} height={'90%'} fullScreen={isMedium || isSmall || isXSmall} showTitle={true} />
            <Form>
              <GroupItem colCount={1}>
                <FormItem dataField='Es_Pd_Id' editorType='dxSelectBox' />
                <FormItem dataField='Es_Aa_Id' editorType='dxSelectBox' />
                <FormItem dataField='Es_Codigo' />
                <FormItem dataField='Es_Nombre' />
                <FormItem dataField='Es_Abreviatura' />
                <FormItem dataField='Es_Homologacion' />
                <FormItem dataField='Es_HomologacionLr' />
                <FormItem dataField='Es_Lr_Id' editorType='dxSelectBox' />
              </GroupItem>
              <GroupItem colCount={1}>
                <FormItem />
                <GroupItem colCount={2}>
                  <FormItem dataField='Es_TiempoDescarte' />
                  <FormItem dataField='Es_OrdenImpresion' />
                </GroupItem>
                <GroupItem colCount={2}>
                  <FormItem dataField='Es_Estado' editorType='dxCheckBox' />
                  <FormItem dataField='Es_Confidencial' editorType='dxCheckBox' />
                </GroupItem>
                <FormItem dataField='Es_CodigoCups' />
                <FormItem dataField='Es_Tecnica' editorType='dxTextArea' editorOptions={{ height: 60 }} />
                <FormItem dataField='Es_Descripcion' editorType='dxTextArea' editorOptions={{ height: 160 }} />
              </GroupItem>
            </Form>
          </Editing>

          <Paging enabled={false} />

          <Toolbar>
            <ToolbarItem location='before'>
              <div className='grid-header'>Estudios</div>
            </ToolbarItem>
            <ToolbarItem name='searchPanel' />
            <ToolbarItem name='addRowButton' />
            <ToolbarItem location='after' locateInMenu='auto' showText='inMenu' widget='dxButton'>
              <Button id='MassiveEditBtn' icon={isMassiveEditMode ? 'close' : 'edit'} text={isMassiveEditMode ? 'Salir Edición Masiva' : 'Edición Masiva'} stylingMode='text' onClick={toggleMassiveEditMode} disabled={isLoading} />
              <Tooltip target={'#MassiveEditBtn'} showEvent='mouseenter' hideEvent='mouseleave' position={'top'}>
                {isMassiveEditMode ? 'Salir del modo edición masiva' : 'Activar modo edición masiva'}
              </Tooltip>
            </ToolbarItem>
            <ToolbarItem location='after' locateInMenu='auto' showText='inMenu' widget='dxButton'>
              <Button id='RefrecarResult' icon='refresh' text='Refresh' stylingMode='text' onClick={handleUpdateData} disabled={isLoading} />
              <Tooltip target={'#RefrecarResult'} showEvent='mouseenter' hideEvent='mouseleave' position={'top'}>
                Actualizar datos
              </Tooltip>
            </ToolbarItem>
          </Toolbar>

          <Column caption='ID' allowEditing={false} dataField='Es_Id' alignment='center' width={80} allowGrouping={false} />
          <Column caption='CÓDIGO' allowEditing={true} dataField='Es_Codigo' alignment='center' width={90} allowGrouping={false}>
            <RequiredRule />
          </Column>
          <Column caption='ESTUDIO' allowEditing={true} dataField='Es_Nombre' alignment='left' allowGrouping={false}>
            <RequiredRule />
          </Column>
          <Column caption='ABREVIATURA' allowEditing={true} dataField='Es_Abreviatura' alignment='center' width={120} allowGrouping={false}>
            <RequiredRule />
          </Column>
          <Column caption='AREA' allowEditing={true} dataField='Es_Aa_Id' alignment='center' width={120} allowGrouping={true} groupIndex={0}>
            <Lookup dataSource={Areas} valueExpr='Aa_Id' displayExpr='Aa_Nombre' />
            <RequiredRule />
          </Column>
          <Column caption='ORDEN' dataField='Es_OrdenImpresion' visible={true} width={70} alignment='center'>
            <RequiredRule />
          </Column>
          <Column caption='L. REMISIÓN' allowEditing={true} dataField='Es_Lr_Id' alignment='center' width={120} allowGrouping={true}>
            <Lookup dataSource={laboratorios} valueExpr='Lr_Id' displayExpr='Lr_Nombre' allowClearing={true} />
          </Column>
          <Column caption='PRIORIDAD' allowEditing={true} dataField='Es_Pd_Id' alignment='center' width={120} allowGrouping={true} visible={false}>
            <Lookup dataSource={prioridades} valueExpr='Pd_Id' displayExpr='Pd_Nombre' />
            <RequiredRule />
          </Column>
          <Column caption='CUPS' allowEditing={true} dataField='Es_CodigoCups' alignment='center' width={80} allowGrouping={false} visible={true} />
          <Column caption='HOMOLOGACION' allowEditing={true} dataField='Es_Homologacion' alignment='center' width={130} allowGrouping={false} />
          <Column caption='HOMOLOGACION L. REMISIÓN' allowEditing={true} dataField='Es_HomologacionLr' alignment='center' width={130} allowGrouping={false} />
          <Column caption='ESTADO' allowEditing={true} dataField='Es_Estado' alignment='center' width={90} allowGrouping={true} />
          <Column caption='CONFIDENCIAL' dataField='Es_Confidencial' visible={false} />

          <Column caption='DESCRIPCIÓN' dataField='Es_Descripcion' visible={false} />
          <Column caption='TÉCNICA' dataField='Es_Tecnica' visible={false} />
          <Column caption='TIEMPO DESCARTE' dataField='Es_TiempoDescarte' visible={false} />

          {!isMassiveEditMode && (
            <Column type='buttons' caption={'ACCIÓN'} width={180} allowGrouping={false}>
              <Butt name='edit' />
              <Butt name='delete' />
              <Butt hint='Configurar' icon='eyeopen' visible={true} disabled={false} onClick={handleDetails} />
              <Butt hint='Duplicar' icon='copy' visible={true} disabled={false} onClick={handleDuplicateClick} />
            </Column>
          )}
        </DataGrid>

        {isModalVisible && <ModalAnalytesByStudy visible={isModalVisible} setVisible={() => setIsModalVisible(false)} IdEstudio={idEstudio} Estudio={estudio} Analitos={analitos} />}
      </div>
    </div>
  );
};

export default Index;
